import { inject } from '../../../../common/container/inject';
import { ILocalization, ILocalizationToken } from '../../../../application/language/ILocalization';
import { IFileOption, ITaskLanguage } from '../../../../service/unit/interface/IUnitPractice';
import { generateUUID } from '../../../../utils/generateUUID';
import { IFile } from '../parts/fileTree/IFileTree';
import { CodeEditorUI } from './CodeEditorUI';
import { ICodeEditorDomain } from './ICodeEditorDomain';
import { ICodeEditorUI } from './ICodeEditorUI';
import { CodeMode } from './ICodeMode';

export class CodeEditorDomain implements ICodeEditorDomain {
  public ui: ICodeEditorUI;
  private i18n: ILocalization = inject<ILocalization>(ILocalizationToken);

  constructor() {
    this.ui = new CodeEditorUI();
  }
  onChangeMode = (mode: string) => {
    this.ui.mode.setValue(CodeMode[mode]);
  };
  openContextMenu = (file: IFile, event: any) => {
    event.preventDefault();
    event.stopPropagation();
    this.ui.currentFile.setValue(file);
    this.ui.isContextOpen.setValue(true);
    this.ui.event?.setValue({ clientX: event.clientX, clientY: event.clientY });
  };
  closeContextMenu = () => {
    this.ui.isContextOpen.setValue(false);
  };
  pickingFile = (file: IFile) => {
    if (file.children?.length === undefined) {
      if (this.ui.mode.value === CodeMode.review) {
        this.ui.currentFile.setValue(file);
        this.ui.currentOption.setValue(null as any);
      }
      if (this.ui.mode.value === CodeMode.writing) {
        this.ui.currentFile.setValue(file);
      }
    }
  };
  pickingOption = (option: IFileOption) => {
    this.ui.currentOption.setValue(option);
  };
  private createOptionName = (options: IFileOption[]) => `${this.i18n.translate('passing.option')} ${options.length + 1}`;

  addOption = (lang: ITaskLanguage) => {
    const newOption: IFileOption = { name: this.createOptionName(lang.options || []), isCorrect: false, value: '' };
    lang.options = [...(lang.options || []), newOption];
    const detectQuestion = (file: IFile) => {
      file.children?.forEach((child) => {
        if (child.isQuestion) {
          child.options = [...(child.options || []), { name: this.createOptionName(child.options || []), isCorrect: false, value: '' }];
        } else {
          detectQuestion(child);
        }
      });
    };

    this.ui.fileTree.list.forEach((file) => detectQuestion(file));
    this.ui.currentOption.setValue(newOption);
  };
  deleteOption = (option: IFileOption, lang: ITaskLanguage) => {
    const optionIndex = lang.options?.findIndex((opt) => opt.name === option.name) ?? -1;
    lang.options = lang.options?.filter((opt) => opt.name !== option.name);

    const detectQuestion = (file: IFile) => {
      file.children?.forEach((child) => {
        if (child.isQuestion) {
          const filteredOptions = (child.options || []).filter((opt) => opt.name !== option.name);
          child.options = [];

          for (let filteredOption of filteredOptions) {
            child.options.push({ ...filteredOption, name: this.createOptionName(child.options) });
          }
        } else {
          detectQuestion(child);
        }
      });
    };
    this.ui.fileTree.list.map((file) => detectQuestion(file));

    if (lang.options && lang.options.length > 0) {
      const newIndex = optionIndex > 0 ? optionIndex - 1 : 0;
      this.ui.currentOption.setValue(lang.options[newIndex]);
    } else {
      this.ui.currentOption.setValue(null as any);
    }
  };
  toggleOption = (name: string) => {
    this.ui.lang.value.options?.forEach((opt) => {
      if (opt.name === name) {
        opt.isCorrect = !opt.isCorrect;
      }
    });
    const detectQuestion = (file: IFile) => {
      file.children?.forEach((child) => {
        if (child.isQuestion) {
          child.options?.forEach((opt) => {
            if (opt.name === name) {
              opt.isCorrect = !opt.isCorrect;
            }
          });
        } else {
          detectQuestion(child);
        }
      });
    };
    this.ui.fileTree.list.map((file) => detectQuestion(file));
  };

  newFolder = (currentFile: IFile) => {
    const findFolder = (file: IFile) => {
      file.children?.forEach((child) => {
        if (child.id === currentFile.id) {
          this.ui.currentFile.setValue(file);
        } else {
          findFolder(child);
        }
      });
    };
    if (currentFile.children) {
      this.ui.currentFile.setValue(currentFile);
    } else {
      this.ui.fileTree.list.map((file) => findFolder(file));
    }
    this.closeContextMenu();
    this.ui.isInputFolderOpen.setValue(true);
    setTimeout(() => document.getElementById('filetree__input')?.focus(), 0);
  };
  creatingNewFolder = (value: string) => {
    const newFolder: IFile = {
      id: generateUUID(),
      name: value,
      children: [],
    };
    this.ui.currentFile.value.children?.push(newFolder);
  };
  newFile = (currentFile: IFile) => {
    const findFolder = (file: IFile) => {
      file.children?.forEach((child) => {
        if (child.id === currentFile.id) {
          this.ui.currentFile.setValue(file);
        } else {
          findFolder(child);
        }
      });
    };
    if (currentFile.children) {
      this.ui.currentFile.setValue(currentFile);
    } else {
      this.ui.fileTree.list.map((file) => findFolder(file));
    }
    this.closeContextMenu();
    this.ui.isInputFileOpen.setValue(true);
    setTimeout(() => document.getElementById('filetree__input_file')?.focus(), 0);
  };
  creatingNewFile = (value: string) => {
    const newFile: IFile = {
      id: generateUUID(),
      name: value,
      value: '',
      options: [],
      isQuestion: false,
    };
    this.ui.currentFile.value.children?.push(newFile);
    this.pickingFile(newFile);
  };

  onDelete = (id: string) => {
    const currentFileId = this.ui.currentFile.value?.id;
    let navigateTo: IFile | null = null;

    const findFolder = (file: IFile) => {
      file.children?.forEach((child, index) => {
        if (child.id === id) {
          if (child.id === currentFileId) {
            const siblings = file.children!;
            if (siblings.length > 1) {
              navigateTo = index > 0 ? siblings[index - 1] : siblings[1];
            } else {
              navigateTo = file;
            }
          }
          file.children?.splice(index, 1);
        } else {
          findFolder(child);
        }
      });
    };
    this.ui.fileTree.list.map((file) => findFolder(file));

    if (navigateTo) {
      if ((navigateTo as IFile).children !== undefined) {
        this.ui.currentFile.setValue(navigateTo);
        this.ui.currentOption.setValue(null as any);
      } else {
        this.pickingFile(navigateTo);
      }
    }

    this.ui.isContextOpen.setValue(false);
  };
  toggleQuestion = (currentFile: IFile) => {
    const newValue = !currentFile.isQuestion;
    currentFile.isQuestion = newValue;
    this.addDefaultOptions(currentFile);

    const syncInTree = (files: IFile[]) => {
      files.forEach((f) => {
        if (f.id === currentFile.id) {
          f.isQuestion = newValue;
        }
        if (f.children) syncInTree(f.children);
      });
    };
    syncInTree(this.ui.fileTree.list);
  };

  private addDefaultOptions = (file: IFile) => {
    if (file.isQuestion) {
      const firstFileWothOptions = this.ui.lang.value.options;

      file.options = []
      if (firstFileWothOptions === undefined || firstFileWothOptions?.length === 0) {
        return;
      }

      for (let i = 0; i < firstFileWothOptions.length; i++) {
        file.options?.push({ name: this.createOptionName(file.options), isCorrect: firstFileWothOptions[i].isCorrect, value: '' });
      }
    } else {
      delete file.options;
    }
  }
  onRename = (currentFile: IFile) => {
    this.closeContextMenu();
    this.ui.isInputRenameOpen.setValue(true);
    setTimeout(() => document.getElementById('file_rename')?.focus(), 0);
  };
  rename = (name: string) => {
    this.ui.currentFile.value.name = name;
  };
  onDuplicate = () => {
    const currentFile = this.ui.currentFile.value;
    if (!currentFile) return;

    const deepCopy = (file: IFile): IFile => {
      const copy: IFile = {
        id: generateUUID(),
        name: file.name + ' (copy)',
        value: file.value,
        isQuestion: file.isQuestion,
      };
      if (file.options) {
        copy.options = file.options.map((opt) => ({ ...opt }));
      }
      if (file.children) {
        copy.children = file.children.map((child) => deepCopy(child));
      }
      return copy;
    };

    const findParentAndInsert = (file: IFile): boolean => {
      if (file.children) {
        const index = file.children.findIndex((child) => child.id === currentFile.id);
        if (index !== -1) {
          const copy = deepCopy(currentFile);
          file.children.splice(index + 1, 0, copy);
          if (!copy.children) {
            this.pickingFile(copy);
          }
          return true;
        }
        for (const child of file.children) {
          if (findParentAndInsert(child)) return true;
        }
      }
      return false;
    };

    this.ui.fileTree.list.forEach((file) => findParentAndInsert(file));
    this.closeContextMenu();
  };
}
